import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import { FooterHomeFragment, FooterQuery } from "../../../graphql-types";
import useCurrentYear from "../../hooks/useCurrentYear";
import ExternalLink from "../ExternalLink";
import { RichTextRenderer } from "../RichTextRenderer";
import { putLinksInThreeColumns } from "./utils";
import InternalLink from "../InternalLink";
import LanguageSelector from "../LanguageSelector";
import "../../css/footer.css";

export type Props = {
  home: FooterHomeFragment;
  hideDisclaimerMessage?: boolean;
};

export default function Footer({ home, hideDisclaimerMessage }: Props) {
  const { unileverLogo, sitemap } = useStaticQuery<FooterQuery>(graphql`
    query Footer {
      home {
        lexicon {
          opensInNewTab
        }
      }
      unileverLogo: file(relativePath: { eq: "unilever-logo.gif" }) {
        publicURL
      }
      sitemap {
        path
        title
      }
    }
  `);

  const currentYear = useCurrentYear();
  const columns = putLinksInThreeColumns(home, sitemap);
  const hasLinks = Boolean(columns.find((col) => col.length > 0));
  const hasSocialLinks = Boolean(
    home.socialChannels && home.socialChannels.length > 0
  );

  const hasLanguages =
    home.domain.languages && home.domain.languages.length > 0;

  const disclaimerMessagePath = home?.disclaimerMessage?.absolutePath;

  return (
    <footer className="footer" data-cy="footer">
      {home.legals?.blurb?.json && (
        <RichTextRenderer
          className="footer__blurb"
          component="div"
          rawContent={JSON.parse(home.legals?.blurb?.json)}
        />
      )}
      {home.brand.disclaimer && !hideDisclaimerMessage && (
        <div className="footer__disclaimer">
          <svg
            width="28"
            height="28"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Warning icon"
          >
            <path
              d="M14 0C6.268 0 0 6.268 0 14s6.268 14 14 14 14-6.268 14-14A14 14 0 0 0 14 0zm.298 22.043a1.787 1.787 0 1 1 0-3.575 1.787 1.787 0 0 1 0 3.575zm0-5.164c-.987 0-1.787-.8-1.787-1.788V7.745a1.787 1.787 0 1 1 3.574 0v7.346c0 .987-.8 1.788-1.787 1.788z"
              fill="#796C3E"
            />
          </svg>
          <span>
            {disclaimerMessagePath && (
              <ExternalLink
                href={disclaimerMessagePath}
                title={home.brand.disclaimer}
                showExternalIcon={false}
              >
                {home.brand.disclaimer}
              </ExternalLink>
            )}
            {!disclaimerMessagePath && home.brand.disclaimer}
          </span>
        </div>
      )}
      {(hasLinks || hasSocialLinks || hasLanguages) && (
        <div className="footer__wrapper">
          {(hasLinks || hasLanguages) && (
            <div className="footer__links">
              {columns.map((column, idx) => (
                <ul key={idx}>
                  {column.map((link) => {
                    const Link = link.external ? ExternalLink : InternalLink;
                    return (
                      <li key={link.title}>
                        <Link href={link.href} title={link.title}>
                          {link.title}
                        </Link>
                      </li>
                    );
                  })}
                  {idx === 2 && hasLanguages && (
                    <li className="footer__language-selector">
                      {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                      <LanguageSelector languages={home.domain.languages!} />
                    </li>
                  )}
                </ul>
              ))}
            </div>
          )}
          {hasSocialLinks && (
            <div className="footer__socials">
              {home.lexicon.socialTitle && (
                <span className="footer__socials-title">
                  {home.lexicon.socialTitle}
                </span>
              )}
              <ul>
                {home.socialChannels?.map((channel) => (
                  <li
                    key={channel?.name}
                    className={channel?.name.toLowerCase()}
                  >
                    <ExternalLink
                      href={channel?.url}
                      title={channel?.name}
                      showExternalIcon={false}
                    >
                      <img
                        width="28"
                        height="28"
                        alt={channel?.image.description || ""}
                        src={channel?.image.file?.fileLocal?.publicURL || ""}
                      />
                    </ExternalLink>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="footer__notices">
        <ExternalLink
          className="footer__unilever-notice"
          href="https://www.unilever.com"
          title="www.unilever.com"
        >
          © Unilever {currentYear}
        </ExternalLink>
        <ul>
          {home.legals?.termsOfUseUrl && home.legals?.termsOfUseLabel && (
            <li>
              <ExternalLink
                href={home.legals.termsOfUseUrl}
                title={home.legals.termsOfUseLabel}
              >
                {home.legals.termsOfUseLabel}
              </ExternalLink>
            </li>
          )}
          {home.legals?.cookieNoticeUrl && home.legals?.cookieNoticeLabel && (
            <li>
              <ExternalLink
                id="_bapw-link"
                href={home.legals.cookieNoticeUrl}
                title={home.legals.cookieNoticeLabel}
              >
                {home.legals.cookieNoticeLabel}
              </ExternalLink>
            </li>
          )}
          {home.legals?.privacyNoticeUrl && home.legals?.privacyNoticeLabel && (
            <li>
              <ExternalLink
                href={home.legals.privacyNoticeUrl}
                title={home.legals.privacyNoticeLabel}
              >
                {home.legals.privacyNoticeLabel}
              </ExternalLink>
            </li>
          )}
        </ul>
      </div>
      <div className="footer__logos">
        <ExternalLink
          href="https://www.unilever.com"
          showExternalIcon={false}
          title="www.unilever.com"
        >
          <img
            width="96"
            height="96"
            alt="Unilever Logo"
            src={unileverLogo?.publicURL || ""}
            loading="lazy"
          />
        </ExternalLink>
      </div>
    </footer>
  );
}

export const fragments = {
  home: graphql`
    fragment FooterHome on home {
      brand {
        disclaimer
      }
      disclaimerMessage {
        absolutePath
      }
      domain {
        languages {
          ...LanguageSelectorLanguage
        }
      }
      lexicon {
        socialTitle
        opensInNewTab
      }
      socialChannels {
        name
        url
        image {
          description
          file {
            fileLocal {
              publicURL
            }
          }
        }
      }
      legals {
        blurb {
          json
        }
        additionalMessages {
          id
          slug
          title
        }
        cookieNoticeLabel
        cookieNoticeUrl
        privacyNoticeLabel
        privacyNoticeUrl
        termsOfUseLabel
        termsOfUseUrl
        whatsInOurProductsLabel
        whatsInOurProductsUrl
        protectionOfPersonalDataLabel
        protectionOfPersonalDataUrl
        legalWarningLabel
        legalWarningUrl
      }
      faq {
        path
        title
      }
      footerLinks {
        ... on Hyperlink {
          id
          title
          path: url
        }
        ... on Product {
          id
          title: name
          path
        }
        ... on ContactUs {
          id
          title
          path
        }
        ... on message {
          id
          title
          path
        }
        ... on topic {
          id
          title
          path
        }
        ... on SustainabilityCategory {
          id
          title
          path
        }
        ... on ContentfulCategory {
          id
          title: pageTitle
          path
        }
        ... on SustainabilityLandingPage {
          id
          title
          path
        }
        ... on ContentfulArticle {
          id
          title: pageTitle
          path
        }
        ... on Sitemap {
          id
          title
          path
        }
      }
      additionalExternalLinks {
        id
        title
        url
      }
    }
  `,
};
